import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useUpdateMerchantSettings } from '@/api/domains/merchant-settings';
import { Input } from '@/components/ui/input';
import { toApiProblem, toastProblem } from '@/lib/errors';

import {
  InlineControlRow,
  SettingsToggleRow,
  inlineNumberInputClass,
} from '../components/SettingsSection';

// Keep in sync with config('carrier.limits.point_edit_cutoff_minutes_max') on the backend.
const MIN_CUTOFF_MINUTES = 1;
const MAX_CUTOFF_MINUTES = 10080;

interface PointEditCutoffSectionProps {
  cutoffEnabled: boolean;
  cutoffMinutes: number;
}

/**
 * Limits how long after checkout a buyer may change their pickup point. Saves
 * immediately - the toggle on click, the minutes field on blur (disjoint
 * mutations so neither blocks the other).
 */
export function PointEditCutoffSection({
  cutoffEnabled,
  cutoffMinutes,
}: PointEditCutoffSectionProps) {
  const { t } = useTranslation('settings');

  const updateEnabled = useUpdateMerchantSettings();
  const updateMinutes = useUpdateMerchantSettings();

  const [pendingEnabled, setPendingEnabled] = useState<boolean | null>(null);
  const enabled = pendingEnabled ?? cutoffEnabled;

  const [minutesInput, setMinutesInput] = useState(String(cutoffMinutes));

  useEffect(() => {
    if (pendingEnabled !== null && pendingEnabled === cutoffEnabled) {
      setPendingEnabled(null);
    }
  }, [pendingEnabled, cutoffEnabled]);

  // Re-sync the input when saved settings arrive/refresh.
  useEffect(() => {
    setMinutesInput(String(cutoffMinutes));
  }, [cutoffMinutes]);

  const handleToggle = (checked: boolean) => {
    if (updateEnabled.isPending) return;
    setPendingEnabled(checked);
    updateEnabled.mutate(
      { settings: { point_edit_cutoff_enabled: checked } },
      {
        onSuccess: () => toast.success(t('saved')),
        onError: (error) => {
          setPendingEnabled(null);
          toastProblem(toApiProblem(error));
        },
      },
    );
  };

  const saveMinutes = () => {
    const parsed = Number.parseInt(minutesInput, 10);
    if (!Number.isFinite(parsed) || parsed < MIN_CUTOFF_MINUTES) {
      setMinutesInput(String(cutoffMinutes));
      return;
    }
    const clamped = Math.min(parsed, MAX_CUTOFF_MINUTES);
    setMinutesInput(String(clamped));
    if (clamped === cutoffMinutes) return;
    updateMinutes.mutate(
      { settings: { point_edit_cutoff_minutes: clamped } },
      {
        onSuccess: () => toast.success(t('saved')),
        onError: (error) => {
          setMinutesInput(String(cutoffMinutes));
          toastProblem(toApiProblem(error));
        },
      },
    );
  };

  return (
    <SettingsToggleRow
      label={t('checkout.pointEditCutoff.label')}
      description={t('checkout.pointEditCutoff.description')}
      checked={enabled}
      onCheckedChange={handleToggle}
    >
      {enabled ? (
        <InlineControlRow>
          <span>{t('checkout.pointEditCutoff.inlineAllowFor')}</span>
          <Input
            type="number"
            inputMode="numeric"
            min={MIN_CUTOFF_MINUTES}
            max={MAX_CUTOFF_MINUTES}
            value={minutesInput}
            onChange={(event) => setMinutesInput(event.target.value)}
            onBlur={saveMinutes}
            aria-label={t('checkout.pointEditCutoff.minutesLabel')}
            className={inlineNumberInputClass}
          />
          <span>{t('checkout.pointEditCutoff.inlineMinutes')}</span>
        </InlineControlRow>
      ) : null}
    </SettingsToggleRow>
  );
}
